import * as React from 'react';
import { z } from 'zod';
import { gql } from '@apollo/client';
import {
  CarViewFragment,
  useSuggestCarEditLazyQuery,
} from './__generated__/CarView.generated';
import { CarViewSuggestionText } from './CarViewSuggestionText';
import {
  CarEditSuggestionInput,
  UpdateCarInput,
} from 'src/graphql/__generated__/globalTypes';
import { Button } from 'src/shadcn/components/ui/button';
import { RotateCw } from 'lucide-react';
import { CarViewVinDecodeDialog } from './CarViewVinDecodeDialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'src/shadcn/components/ui/form';
import { useHandleOnGoingAway } from 'src/utils/dirtyFormNavigationHook';
import { Input } from 'src/shadcn/components/ui/input';
import { CarMakeSelector } from './CarMakeSelector';
import { CarModelSelector } from './CarModelSelector';

export interface IAddCarPageProps {
  car: CarViewFragment;
  onSaveCar: (car: UpdateCarInput) => void;
  isSavingCar: boolean;
}

export const CarView: React.FunctionComponent<IAddCarPageProps> = props => {
  const { form } = useCarViewForm(props);
  useHandleOnGoingAway({
    isDirty: form.formState.isDirty,
    warningText:
      'You have unsaved changes - are you sure you wish to leave this page?',
  });

  const { suggestCarEdit, carEditSuggestion } = useSuggestCarEdit(form, () => {
    setShowSuggestions(true);
  });
  const [showSuggestions, setShowSuggestions] = React.useState(false);

  // useAutosave({
  //   data: watchVin,
  //   onSave: car => {
  //     if (car.vin?.length !== 17) {
  //       suggestCarEdit({
  //         id: car.id,
  //         make: car.make,
  //         model: car.model,
  //         vin: car.vin,
  //         year: car.year ? String(car.year) : undefined,
  //       });
  //     }
  //   },
  //   interval: 1000,
  // });
  const [carVinDecoderDialogOpen, setCarVinDecoderDialogOpen] =
    React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const watchMake = form.watch('make');
  return (
    <div ref={containerRef}>
      <div className='flex flex-col space-y-5'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(data => {
              props.onSaveCar(data);
              form.reset(data, { keepValues: false });
              //TODO Handle error case
            })}
            className='space-y-6 md:w-2/3'
          >
            <div className='flex items-end gap-3'>
              <FormField
                control={form.control}
                name='vin'
                render={({ field }) => (
                  <FormItem className='flex flex-grow flex-col'>
                    <FormLabel>Vin</FormLabel>
                    <FormControl>
                      <Input
                        placeholder=''
                        {...field}
                        onChange={v => {
                          const newVin = v.target.value;
                          if (newVin.length === 17) {
                            const car = form.getValues();
                            suggestCarEdit({
                              id: car.id,
                              make: car.make,
                              model: car.model,
                              vin: newVin,
                              year: car.year ? String(car.year) : undefined,
                            });
                          }
                          field.onChange(newVin);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CarViewVinDecodeDialog
                vin={form.getValues('vin')}
                open={carVinDecoderDialogOpen}
                onOpenChange={setCarVinDecoderDialogOpen}
              />
            </div>
            <FormField
              control={form.control}
              name='make'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Make</FormLabel>
                  <div>
                    <CarMakeSelector
                      value={field.value}
                      setValue={newValue => {
                        field.onChange(newValue);
                        if (newValue !== field.value) {
                          form.setValue('model', '');
                        }
                      }}
                      containerRef={containerRef}
                    />
                  </div>
                  <FormDescription>
                    <CarViewSuggestionText
                      onClick={() => {
                        if (carEditSuggestion?.make) {
                          form.setValue('make', carEditSuggestion.make);
                        }
                      }}
                      suggestedValue={
                        showSuggestions &&
                        field.value !== carEditSuggestion?.make
                          ? carEditSuggestion?.make
                          : undefined
                      }
                      currentValue={form.getValues('make')}
                    />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='model'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model</FormLabel>
                  <CarModelSelector
                    key={watchMake}
                    value={field.value}
                    setValue={field.onChange}
                    containerRef={containerRef}
                    makeId={watchMake}
                  />
                  <FormDescription>
                    <CarViewSuggestionText
                      onClick={() => {
                        if (carEditSuggestion?.model) {
                          form.setValue('model', carEditSuggestion.model);
                        }
                      }}
                      suggestedValue={
                        showSuggestions &&
                        field.value !== carEditSuggestion?.model
                          ? carEditSuggestion?.model
                          : undefined
                      }
                      currentValue={form.getValues().model}
                    />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='trim'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trim</FormLabel>
                  <FormControl>
                    <Input placeholder='' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='year'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Year</FormLabel>
                  <FormControl>
                    <Input placeholder='' {...field} />
                  </FormControl>
                  <FormDescription>
                    <CarViewSuggestionText
                      onClick={() => {
                        if (carEditSuggestion?.year) {
                          form.setValue('year', Number(carEditSuggestion.year));
                        }
                      }}
                      suggestedValue={
                        showSuggestions &&
                        field.value !== carEditSuggestion?.year &&
                        carEditSuggestion?.year
                          ? String(carEditSuggestion.year)
                          : undefined
                      }
                      currentValue={
                        form.getValues().year
                          ? String(form.getValues().year)
                          : ''
                      }
                    />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='colour'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Colour</FormLabel>
                  <FormControl>
                    <Input placeholder='' {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='licensePlate'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>License plate</FormLabel>
                  <FormControl>
                    <Input placeholder='' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              role='button'
              type='submit'
              id='save-car'
              disabled={!form.formState.isDirty}
            >
              <span>Save</span>
              {props.isSavingCar && (
                <RotateCw className='ml-2 h-4 w-4 animate-spin' />
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

function useSuggestCarEdit(
  form: ReturnType<typeof useCarViewForm>['form'],
  onSuggestionReceived: () => void
) {
  const [suggestCarEdit, { data }] = useSuggestCarEditLazyQuery({
    onError: e => {
      // We want to be silent with errors.
    },
    fetchPolicy: 'network-only',
  });
  return {
    suggestCarEdit: (car: CarEditSuggestionInput) => {
      suggestCarEdit({
        variables: { car },
        onCompleted: data => {
          const suggestion = data?.suggestCarEdit;
          if (suggestion?.year && !form.getValues('year')) {
            form.setValue('year', suggestion.year);
          }
          if (suggestion?.make && !form.getValues('make')) {
            form.setValue('make', suggestion.make);
          }
          if (suggestion?.model && !form.getValues('model')) {
            form.setValue('model', suggestion.model);
          }
          onSuggestionReceived();
        },
      });
    },
    carEditSuggestion: data?.suggestCarEdit,
  };
}

const SUGGEST_CAR_EDIT_QUERY = gql`
  query SuggestCarEdit($car: CarEditSuggestionInput!) {
    suggestCarEdit(car: $car) {
      suggestedVin
      make
      model
      year
    }
  }
`;

const fragment = gql`
  fragment CarView on Car {
    id
    year
    make
    model
    colour
    vin
    licensePlate
    trim
  }
`;

function useCarViewForm(props: IAddCarPageProps | undefined) {
  const form = useForm({
    resolver: zodResolver(
      z.object({
        id: z.string(),
        make: z.string(),
        model: z.string(),
        trim: z.string(),
        year: z.coerce
          .number()
          .min(1900)
          .max(new Date().getFullYear() + 2),
        colour: z.string(),
        licensePlate: z.string(),
        vin: z.string(),
      })
    ),
    defaultValues: {
      id: props?.car.id || '',
      make: props?.car.make || '',
      model: props?.car.model || '',
      year: props?.car.year || new Date().getFullYear(),
      colour: props?.car.colour || '',
      licensePlate: props?.car.licensePlate || '',
      vin: props?.car.vin || '',
      trim: props?.car.trim || '',
    },
  });
  return { form };
}
